// Step 1 of the property wizard — "Thông tin cơ bản".
//
// Fields:
//   - project / building (required — a project picker on create; a locked
//     value when the unit is already linked to a project on edit; otherwise a
//     free building-name input)
//   - streetAddress      (free text; locked + prefilled from a linked project)
//   - wardCode           (required, select; locked + prefilled from a project)
//   - propertyType       (required, chip picker)
//   - listingType        (required, segmented toggle)
//   - internalCode       (free text, auto if blank; auto-numbered by a project)
//   - assignedBrokerId   (broker dropdown)
//
// When a project is chosen, its shared fields (name, ward, address, total
// floors) are the single source of truth: they're prefilled and locked here,
// and the internal code is auto-numbered from the project's prefix.

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { useAuthStore } from "@/lib/auth-store";
import { WARD_OPTIONS } from "@/features/property/utils/ward-options";
import { WardCombobox } from "@/components/ward-combobox";
import { PROPERTY_TYPE_OPTIONS } from "@/features/property/utils/property-enum-options";
import { useProject, useProjectNextCode } from "@/features/project/api";
import { ProjectPickerField } from "@/features/property/wizard/project-picker-field";
import {
  WizardChipPicker,
  WizardField,
} from "@/features/property/wizard/wizard-field";
import { cn } from "@/lib/utils";
import type {
  PropertyDraft,
  StepValidation,
} from "@/features/property/wizard/wizard-state";

const LISTING_TYPE_OPTIONS = [
  { value: "SALE" as const, label: "Bán" },
  { value: "RENT" as const, label: "Cho thuê" },
];

// The property's assigned broker is the "Source" who liaises with the real
// owner, so only Source / Manager / Owner can hold a listing. Agents work the
// buyer side (leads) and Viewers are read-only — both are excluded from the
// "Người phụ trách" picker.
const ASSIGNABLE_BROKER_ROLES = new Set(["OWNER", "MANAGER", "SOURCE"]);

interface BasicInfoStepProps {
  draft: PropertyDraft;
  onChange: (next: Partial<PropertyDraft>) => void;
  validation: StepValidation;
  mode: "create" | "edit";
}

export function BasicInfoStep({
  draft,
  onChange,
  validation,
  mode,
}: BasicInfoStepProps) {
  const brokersQuery = useBrokers();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const errors = validation.errors;

  // Shared-field state driven by the linked project. When a field is defined on
  // the project it's locked here (the project is the source of truth); an
  // undefined project field stays editable so a partly-filled project doesn't
  // block the unit.
  const isLinked = draft.projectId != null;
  const linkedProject = useProject(draft.projectId).data ?? null;
  const wardLocked = isLinked && linkedProject?.wardCode != null;
  const addressLocked = isLinked && linkedProject?.streetAddress != null;
  const codeAuto = isLinked && Boolean(linkedProject?.codePrefix);
  const nextCodeQuery = useProjectNextCode(draft.projectId, codeAuto);

  // Listing type is frozen while a deposit is pending — the backend rejects the
  // change (409), so lock the toggle instead of letting the save fail. Only
  // relevant on edit; create has no status yet.
  const listingTypeLocked = draft.status === "DEPOSIT";
  const autoCode = nextCodeQuery.data?.internalCode ?? "";
  const wardLabel =
    WARD_OPTIONS.find((w) => w.value === draft.wardCode)?.label ??
    draft.wardCode ??
    "";

  // Only Source / Manager / Owner can be assigned to a listing (see the
  // ASSIGNABLE_BROKER_ROLES note); agents + viewers are filtered out. The
  // broker already on the draft is always kept, so editing a legacy listing
  // assigned to someone now-excluded still shows who's on it (rather than a
  // blank picker) — the user can keep them or reassign to a valid role.
  const assignableBrokers = (brokersQuery.data ?? []).filter(
    (b) => ASSIGNABLE_BROKER_ROLES.has(b.role) || b.id === draft.assignedBrokerId,
  );

  // Default the assigned broker to the logged-in user when the draft
  // hasn't picked one yet — matches the design comp's "defaultValue".
  const brokerValue = draft.assignedBrokerId ?? currentUserId ?? undefined;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="border-b border-border pb-2 text-[16px] font-bold tracking-tight">
        Thông tin cơ bản
      </h2>

      <WizardField
        label="Dự án / toà nhà"
        required
        {...(errors.buildingName !== undefined ? { error: errors.buildingName } : {})}
      >
        {(id) =>
          mode === "create" ? (
            <ProjectPickerField
              draft={draft}
              onChange={onChange}
              invalid={errors.buildingName !== undefined}
            />
          ) : isLinked ? (
            <Input
              id={id}
              value={linkedProject?.name ?? draft.buildingName ?? ""}
              disabled
              readOnly
              aria-label="Dự án"
            />
          ) : (
            <Input
              id={id}
              placeholder="VD: Tây Hồ Residence, Sky Garden Tower…"
              value={draft.buildingName ?? ""}
              onChange={(e) => onChange({ buildingName: e.target.value })}
            />
          )
        }
      </WizardField>

      {isLinked ? (
        <p className="-mt-3 text-[12px] text-muted-foreground">
          Căn hộ thuộc dự án — các trường dùng chung được lấy từ dự án và tự cập
          nhật khi dự án thay đổi.
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <WizardField label="Địa chỉ" {...(addressLocked ? { hint: "Từ dự án" } : {})}>
          {(id) =>
            addressLocked ? (
              <Input
                id={id}
                value={draft.streetAddress ?? ""}
                disabled
                readOnly
                aria-label="Địa chỉ"
              />
            ) : (
              <Input
                id={id}
                placeholder="Số nhà, tên đường"
                value={draft.streetAddress ?? ""}
                onChange={(e) => onChange({ streetAddress: e.target.value })}
              />
            )
          }
        </WizardField>

        <WizardField
          label="Khu vực"
          required
          {...(wardLocked
            ? { hint: "Từ dự án" }
            : errors.wardCode !== undefined
              ? { error: errors.wardCode }
              : {})}
        >
          {(id) =>
            wardLocked ? (
              <Input id={id} value={wardLabel} disabled readOnly aria-label="Khu vực" />
            ) : (
              <WardCombobox
                id={id}
                value={draft.wardCode ?? null}
                onChange={(v) => onChange({ wardCode: v ?? undefined })}
                placeholder="Tây Hồ, Hoàn Kiếm…"
              />
            )
          }
        </WizardField>
      </div>

      <WizardField
        label="Loại bất động sản"
        required
        {...(errors.propertyType !== undefined ? { error: errors.propertyType } : {})}
      >
        <WizardChipPicker
          ariaLabel="Loại bất động sản"
          options={PROPERTY_TYPE_OPTIONS}
          {...(draft.propertyType !== undefined ? { value: draft.propertyType } : {})}
          onChange={(v) => onChange({ propertyType: v })}
        />
      </WizardField>

      <WizardField
        label="Hình thức giao dịch"
        required
        {...(listingTypeLocked ? { hint: "Không thể đổi khi đang có cọc" } : {})}
        {...(errors.listingType !== undefined ? { error: errors.listingType } : {})}
      >
        <ListingTypeToggle
          {...(draft.listingType !== undefined ? { value: draft.listingType } : {})}
          onChange={(v) => onChange({ listingType: v })}
          disabled={listingTypeLocked}
        />
      </WizardField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <WizardField
          label="Mã BĐS (tuỳ chọn)"
          hint={codeAuto ? "Tự đánh số theo dự án" : "Tự tạo nếu bỏ trống"}
        >
          {(id) =>
            codeAuto ? (
              <Input
                id={id}
                value={autoCode || "Đang tạo mã…"}
                disabled
                readOnly
                aria-label="Mã BĐS"
              />
            ) : (
              <Input
                id={id}
                placeholder="VD: BDS-0001"
                value={draft.internalCode ?? ""}
                onChange={(e) =>
                  onChange({ internalCode: e.target.value.toUpperCase() })
                }
              />
            )
          }
        </WizardField>

        <WizardField label="Người phụ trách">
          <Select
            value={brokerValue !== undefined ? String(brokerValue) : ""}
            onValueChange={(v) =>
              onChange({ assignedBrokerId: v === "" ? undefined : Number(v) })
            }
            disabled={brokersQuery.isPending}
          >
            <SelectTrigger aria-label="Người phụ trách">
              <SelectValue placeholder="Chọn người phụ trách" />
            </SelectTrigger>
            <SelectContent>
              {assignableBrokers.map((b) => (
                <SelectItem key={b.id} value={String(b.id)}>
                  {b.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </WizardField>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Listing-type segmented toggle (Bán / Cho thuê)
// ---------------------------------------------------------------------------

function ListingTypeToggle({
  value,
  onChange,
  disabled = false,
}: {
  value?: "SALE" | "RENT";
  onChange: (next: "SALE" | "RENT") => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        "inline-flex w-fit gap-0.5 rounded-[10px] bg-muted p-[3px]",
        disabled && "opacity-60",
      )}
    >
      {LISTING_TYPE_OPTIONS.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-[8px] px-6 py-1.5 text-[14px] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled && "cursor-not-allowed",
              isActive
                ? "bg-card font-semibold text-foreground shadow-[var(--shadow-soft)]"
                : "font-medium text-muted-foreground",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
